import type { LicenseTier } from '@/mocks/saLicensing';
import { licenseTypeLabels, licenseTypeIcons, formatStorage, formatBandwidth, formatUsers, formatRadios, formatBitrate } from '@/mocks/saLicensing';

interface LicensePublicPreviewModalProps {
  license: LicenseTier;
  onClose: () => void;
}

function priceLabel(lic: LicenseTier): string {
  if (lic.priceMonthly > 0) return `$${lic.priceMonthly}/mes`;
  if (lic.priceAnnual > 0) return `$${lic.priceAnnual}/año`;
  if (lic.priceLifetime > 0) return `$${lic.priceLifetime} único`;
  return 'Gratis';
}

export default function LicensePublicPreviewModal({ license, onClose }: LicensePublicPreviewModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${license.color}14` }}>
              <i className={`${licenseTypeIcons[license.type]} text-lg`} style={{ color: license.color }}></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Vista Previa Pública</h3>
              <p className="text-foreground-400 text-[10px]">Así se verá {license.name} para los usuarios</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Preview Card */}
        <div className="p-5">
          <div className="border-2 rounded-2xl p-6 text-center" style={{ borderColor: license.color }}>
            {/* Badge */}
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
              style={{ backgroundColor: `${license.color}14`, color: license.color }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: license.color }}></span>
              {license.name}
            </span>

            {/* Price */}
            <div className="text-foreground-950 font-bold text-3xl mb-1">{priceLabel(license)}</div>
            <p className="text-foreground-400 text-xs mb-4">{licenseTypeLabels[license.type]}</p>

            {/* Trial badge */}
            {license.trialDays > 0 && (
              <div className="mb-4 p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 inline-flex items-center gap-2 mx-auto">
                <i className="ri-timer-line text-emerald-600 text-sm"></i>
                <span className="text-emerald-700 text-xs font-bold">{license.trialDays} días gratis</span>
              </div>
            )}

            {/* Description */}
            <p className="text-foreground-500 text-xs leading-relaxed mb-5 max-w-sm mx-auto">{license.description}</p>

            {/* Feature grid */}
            <div className="grid grid-cols-2 gap-3 mb-5 text-left">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                <i className="ri-radio-line text-foreground-400 text-xs"></i>
                <span className="text-foreground-600 text-[10px] font-medium">{formatRadios(license.maxRadios)} radios</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                <i className="ri-user-line text-foreground-400 text-xs"></i>
                <span className="text-foreground-600 text-[10px] font-medium">{formatUsers(license.maxUsers)} usuarios</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                <i className="ri-hard-drive-2-line text-foreground-400 text-xs"></i>
                <span className="text-foreground-600 text-[10px] font-medium">{formatStorage(license.storageGB)} storage</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                <i className="ri-speed-up-line text-foreground-400 text-xs"></i>
                <span className="text-foreground-600 text-[10px] font-medium">{formatBandwidth(license.bandwidthGB)}</span>
              </div>
            </div>

            {/* Features list */}
            <div className="space-y-1.5 mb-5 text-left">
              <p className="text-foreground-400 text-[10px] font-semibold mb-1">Incluye</p>
              {[
                { label: 'Streaming', val: license.streaming },
                { label: 'Marketplace', val: license.marketplace },
                { label: 'TV Channel', val: license.tv },
                { label: 'Apps Móviles', val: license.apps },
                { label: 'Inteligencia Artificial', val: license.ai },
                { label: 'Publicidad', val: license.advertising },
                { label: 'Analytics', val: license.analytics },
                { label: 'API', val: license.api },
                { label: 'Dominio propio', val: license.customDomain },
                { label: 'Verificación', val: license.verification },
                { label: 'Frecuencia preferida', val: license.frequencyPreferred },
              ].map((f) => (
                <div key={f.label} className="flex items-center gap-2">
                  <i className={`${f.val ? 'ri-check-line text-emerald-500' : 'ri-close-line text-foreground-300'} text-xs`}></i>
                  <span className={`text-[10px] ${f.val ? 'text-foreground-700' : 'text-foreground-300'}`}>
                    {f.label}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA placeholder */}
            <button
              className="w-full py-3 rounded-xl text-xs font-bold cursor-default opacity-70"
              style={{ backgroundColor: license.color, color: '#fff' }}
            >
              Contratar {license.name}
            </button>
            <p className="text-foreground-300 text-[9px] mt-2">Este es un botón de demostración</p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar vista previa
          </button>
        </div>
      </div>
    </div>
  );
}